/**
 * SharePage — public client-facing document share page.
 * Route: /share?id=recXXX
 *
 * No authentication required. Fetches milestone metadata + documents
 * from the public /api/share endpoint and displays them with AJ branding.
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/* ── File type badge helpers ──────────────────────────────────────────────── */

const TYPE_LABELS = {
  pdf:  'PDF',
  doc:  'DOC',
  docx: 'DOC',
  xls:  'XLS',
  xlsx: 'XLS',
  ppt:  'PPT',
  pptx: 'PPT',
  dwg:  'DWG',
  png:  'IMG',
  jpg:  'IMG',
  jpeg: 'IMG',
  link: 'LINK',
}

function badgeLabel(doc) {
  const ext = (doc.file_type || '').toLowerCase().replace(/^\./, '')
  return TYPE_LABELS[ext] || ext.toUpperCase() || 'FILE'
}

const BADGE_COLORS = {
  PDF:  { bg: '#fdecea', color: '#c0392b' },
  DOC:  { bg: '#e8f0fe', color: '#1a56db' },
  XLS:  { bg: '#e6f4ea', color: '#1e7e34' },
  PPT:  { bg: '#fff3e0', color: '#e65100' },
  DWG:  { bg: '#f3e5f5', color: '#7b1fa2' },
  IMG:  { bg: '#e8f5e9', color: '#2e7d32' },
  LINK: { bg: '#e3f2fd', color: '#1565c0' },
}

function badgeStyle(label) {
  const c = BADGE_COLORS[label] || { bg: '#f0f4f5', color: '#07272D' }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 7px',
    borderRadius: '4px',
    fontFamily: "'DM Mono', monospace",
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    background: c.bg,
    color: c.color,
    flexShrink: 0,
    userSelect: 'none',
  }
}

/* ── Document row ─────────────────────────────────────────────────────────── */

function DocRow({ doc }) {
  const label = badgeLabel(doc)
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '13px 16px',
        background: '#ffffff',
        border: '0.5px solid rgba(7,39,45,0.12)',
        borderRadius: '8px',
        textDecoration: 'none',
        color: '#07272D',
        transition: 'border-color 0.12s, box-shadow 0.12s',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(7,39,45,0.28)'
        e.currentTarget.style.boxShadow = '0 1px 6px rgba(7,39,45,0.07)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'rgba(7,39,45,0.12)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <span style={badgeStyle(label)}>{label}</span>
      <span style={{
        flex: 1,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '14px',
        fontWeight: 450,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {doc.name}
      </span>
      <span style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '12px',
        color: 'rgba(7,39,45,0.4)',
        flexShrink: 0,
      }}>
        Open ↗
      </span>
    </a>
  )
}

/* ── Main page ────────────────────────────────────────────────────────────── */

export default function SharePage() {
  const [params] = useSearchParams()
  const milestoneId = params.get('id')

  const [state, setState] = useState('loading') // 'loading' | 'ready' | 'error'
  const [milestone, setMilestone] = useState(null)
  const [documents, setDocuments] = useState([])
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!milestoneId) {
      setErrorMsg('No milestone ID provided.')
      setState('error')
      return
    }
    fetch(`/api/share?id=${encodeURIComponent(milestoneId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          setErrorMsg(data.error)
          setState('error')
        } else {
          setMilestone(data.milestone)
          setDocuments(data.documents || [])
          setState('ready')
        }
      })
      .catch(err => {
        setErrorMsg(err.message || 'Failed to load.')
        setState('error')
      })
  }, [milestoneId])

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f4f7f8',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Top bar */}
      <div style={{
        background: '#07272D',
        padding: '0 32px',
        height: '52px',
        display: 'flex',
        alignItems: 'center',
      }}>
        <span style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: '13px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: '#ffffff',
        }}>
          Alder James
        </span>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '640px',
        margin: '0 auto',
        padding: '48px 24px 80px',
      }}>
        {state === 'loading' && (
          <p style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '13px',
            color: 'rgba(7,39,45,0.4)',
            margin: 0,
          }}>
            Loading…
          </p>
        )}

        {state === 'error' && (
          <div style={{
            background: '#fdecea',
            border: '0.5px solid rgba(192,57,43,0.25)',
            borderRadius: '8px',
            padding: '16px 20px',
          }}>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14px',
              color: '#c0392b',
              margin: 0,
            }}>
              {errorMsg || 'This link is invalid or the milestone could not be found.'}
            </p>
          </div>
        )}

        {state === 'ready' && milestone && (
          <>
            {/* Header — project / phase / milestone */}
            <div style={{ marginBottom: '36px' }}>
              {milestone.projectName && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'rgba(7,39,45,0.5)',
                  margin: '0 0 6px',
                }}>
                  {milestone.projectName}
                </p>
              )}
              <h1 style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: '22px',
                color: '#07272D',
                margin: '0 0 6px',
                lineHeight: 1.25,
              }}>
                {milestone.name}
              </h1>
              {milestone.phase && (
                <p style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '11px',
                  color: 'rgba(7,39,45,0.45)',
                  margin: 0,
                  letterSpacing: '0.02em',
                }}>
                  {milestone.phase}
                </p>
              )}
            </div>

            {/* Divider */}
            <div style={{
              borderTop: '0.5px solid rgba(7,39,45,0.12)',
              marginBottom: '24px',
            }} />

            {/* Document count label */}
            <p style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '10px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(7,39,45,0.4)',
              margin: '0 0 14px',
            }}>
              {documents.length === 0
                ? 'Documents'
                : documents.length === 1
                  ? '1 Document'
                  : `${documents.length} Documents`}
            </p>

            {/* Document list */}
            {documents.length === 0 ? (
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14px',
                color: 'rgba(7,39,45,0.4)',
                margin: 0,
              }}>
                No documents have been shared for this milestone yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {documents.map(doc => (
                  <DocRow key={doc.id} doc={doc} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '12px 32px',
        borderTop: '0.5px solid rgba(7,39,45,0.10)',
        background: '#f4f7f8',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        <span style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: '10px',
          color: 'rgba(7,39,45,0.35)',
          letterSpacing: '0.03em',
        }}>
          Alder James · Shared via AlderCortex
        </span>
      </div>
    </div>
  )
}
